// src/hooks/useBilling.tsx
/**
 * React hooks for billing operations:
 * - useOrganization: Get current organization details
 * - useUsage: Get usage and quota data
 * - useSubscriptionTiers: Get available subscription tiers
 * - useBilling: Combined hook for billing operations
 */

import { useState, useEffect, useCallback } from "react";
import api from "../api/api";
import { useAuth } from "./useAuth";
import logger from "utils/logger";

// ============================================================================
// Types
// ============================================================================

export interface SubscriptionTier {
  name: string;
  display_name: string;
  description: string;
  price_monthly_cents: number;
  price_monthly: string;
  max_patients: number;
  max_documents_per_month: number;
  max_tokens_per_month: number;
  max_voice_minutes_per_month: number;
  max_users: number;
  max_environments: number;
  can_edit_records: boolean;
  can_use_ai_extraction: boolean;
  can_use_voice: boolean;
  can_export_csv: boolean;
  can_export_api: boolean;
  can_use_api: boolean;
  can_cross_org_share: boolean;
  has_analytics_view: boolean;
  has_analytics_filter: boolean;
  has_sso: boolean;
  has_audit_logs: boolean;
  has_priority_support: boolean;
  trial_days: number;
}

export interface OrganizationMember {
  id: string;
  user_email: string;
  user_name: string;
  role: "owner" | "admin" | "member" | "viewer";
  invited_at: string;
  accepted_at: string | null;
  is_active: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  tier: SubscriptionTier;
  subscription_status: "active" | "trialing" | "past_due" | "canceled" | "incomplete";
  subscription_period_start: string | null;
  subscription_period_end: string | null;
  overage_enabled: boolean;
  is_active: boolean;
  is_trialing: boolean;
  trial_days_remaining: number;
  trial_end: string | null;
  members: OrganizationMember[];
  created_at: string;
  default_trial_location: string;
  data_processing_region: "eu" | "global";
  /** Org-wide AI state: true if any owned environment has AI extraction enabled. */
  ai_enabled?: boolean;
  /** Whether the subscription tier permits AI extraction at all. */
  ai_available?: boolean;
}

export interface QuotaStatus {
  name: string;
  used: number;
  limit: number;
  remaining: number;
  percentage_used: number;
  is_exceeded: boolean;
  is_unlimited: boolean;
}

export interface UsageData {
  period: {
    start: string;
    end: string;
    days_remaining: number;
  };
  tokens: QuotaStatus;
  documents: QuotaStatus;
  voice_minutes: QuotaStatus;
  tier: {
    name: string;
    display_name: string;
  };
  /** Total estimated cost for the current period, in cents */
  estimated_cost_cents: number;
  overage_enabled: boolean;
  is_trialing: boolean;
  trial_days_remaining: number;
}

export interface UsageHistoryItem {
  date: string;
  tokens: number;
  documents: number;
  voice_minutes: number;
  extractions: number;
  cost_cents: number;
}

export interface UsageBreakdownItem {
  type: string;
  requests: number;
  tokens: number;
  cost_cents: number;
  avg_tokens: number;
}

export interface Invoice {
  id: string;
  stripe_invoice_id: string;
  amount_due: number;
  amount_due_formatted: string;
  amount_paid: number;
  amount_paid_formatted: string;
  amount_remaining: number;
  currency: string;
  status: "draft" | "open" | "paid" | "void" | "uncollectible";
  period_start: string;
  period_end: string;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
}

// ============================================================================
// useSubscriptionTiers Hook
// ============================================================================

export const useSubscriptionTiers = () => {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTiers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/billing/tiers/");
      setTiers(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch subscription tiers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  return { tiers, loading, error, refetch: fetchTiers };
};

// ============================================================================
// useOrganization Hook
// ============================================================================

export const useOrganization = () => {
  const { isAuthenticated } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/billing/organization/");
      setOrganization(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch organization");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const updateSettings = useCallback(async (settings: { name?: string; overage_enabled?: boolean; default_trial_location?: string; data_processing_region?: "eu" | "global"; ai_enabled?: boolean }) => {
    try {
      const response = await api.patch("/billing/organization/settings/", settings);
      setOrganization(response.data);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || "Failed to update settings");
    }
  }, []);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  return {
    organization,
    loading,
    error,
    refetch: fetchOrganization,
    updateSettings,
  };
};

// ============================================================================
// useUsage Hook
// ============================================================================

export const useUsage = () => {
  const { isAuthenticated } = useAuth();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [history, setHistory] = useState<UsageHistoryItem[]>([]);
  const [breakdown, setBreakdown] = useState<UsageBreakdownItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/billing/usage/");
      setUsage(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch usage");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchHistory = useCallback(async (days: number = 30) => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get(`/billing/usage/history/?days=${days}`);
      setHistory(response.data);
    } catch (err: any) {
      logger.error("Failed to fetch usage history:", err);
    }
  }, [isAuthenticated]);

  const fetchBreakdown = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get("/billing/usage/breakdown/");
      setBreakdown(response.data.operations || []);
    } catch (err: any) {
      logger.error("Failed to fetch usage breakdown:", err);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUsage();
    fetchHistory();
    fetchBreakdown();
  }, [fetchUsage, fetchHistory, fetchBreakdown]);

  return {
    usage,
    history,
    breakdown,
    loading,
    error,
    refetch: fetchUsage,
    refetchHistory: fetchHistory,
    refetchBreakdown: fetchBreakdown,
  };
};

// ============================================================================
// useInvoices Hook
// ============================================================================

export const useInvoices = () => {
  const { isAuthenticated } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoices = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/billing/invoices/");
      setInvoices(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return { invoices, loading, error, refetch: fetchInvoices };
};

// ============================================================================
// useBilling Hook (Combined)
// ============================================================================

export const useBilling = () => {
  const { isAuthenticated } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const createCheckout = useCallback(async (tier: string): Promise<string | null> => {
    try {
      setCheckoutLoading(true);
      const response = await api.post("/billing/checkout/", { tier });
      return response.data.checkout_url;
    } catch (err: any) {
      if (err.response?.status === 402 && err.response?.data?.error === "agreement_required") {
        const agreementError = new Error(err.response.data.message) as Error & {
          code: string;
          redirect: string;
        };
        agreementError.code = "agreement_required";
        agreementError.redirect = err.response.data.redirect || "/billing?tab=legal";
        throw agreementError;
      }
      logger.error("Failed to create checkout:", err);
      throw new Error(err.response?.data?.error || "Failed to create checkout session");
    } finally {
      setCheckoutLoading(false);
    }
  }, []);

  const openCustomerPortal = useCallback(async (): Promise<string | null> => {
    try {
      setPortalLoading(true);
      const response = await api.post("/billing/portal/");
      return response.data.portal_url;
    } catch (err: any) {
      logger.error("Failed to open portal:", err);
      throw new Error(err.response?.data?.error || "Failed to open billing portal");
    } finally {
      setPortalLoading(false);
    }
  }, []);

  const cancelSubscription = useCallback(async (): Promise<{ message: string; period_end: string | null }> => {
    try {
      setCancelLoading(true);
      const response = await api.post("/billing/cancel/");
      return response.data;
    } catch (err: any) {
      logger.error("Failed to cancel subscription:", err);
      throw new Error(err.response?.data?.error || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  }, []);

  return {
    isAuthenticated,
    createCheckout,
    openCustomerPortal,
    cancelSubscription,
    checkoutLoading,
    portalLoading,
    cancelLoading,
  };
};

export default useBilling;
